/**
 * The operator avatar, as far as the wire is concerned.
 *
 * A learner's avatar is a seed. The original format is nine integers — one part chosen
 * from each of nine rails: build, kit, head, eyes, face, mouth, accessory, ink
 * tone, plate — drawn as a 16×16 figure on the client from the brand's own
 * ramp (`avatar/README.md`, and the renderer at `web/src/lib/avatar.ts`).
 * The versioned full-body format adds five equipment and motion rails; it keeps
 * the original nine indices in place and is rendered as a 32×40 character.
 * Nothing is uploaded, nothing is fetched, and there is no image anywhere to
 * resize, cache or moderate: the seed *is* the avatar, and it is forty-odd
 * characters of digits and dots.
 *
 * That is why this file is in the protocol and the renderer is not. Both ends
 * have to agree on what a browser may claim — which is the same question
 * `cleanFace` answers for the emoji faces, and it has the same answer: a
 * narrow gate, checked against a rule rather than against a catalogue. The
 * Worker never draws an avatar, so it needs none of the part data; it needs to
 * know that what arrived is a supported tuple of small numbers, not a URL.
 *
 * The rule is deliberately shape-only. An index past the end of its rail is
 * *not* rejected here — the renderer already falls back to part zero for one,
 * because a stored seed has to survive a rail being extended by a build that
 * has not shipped everywhere yet. Checking the catalogue on the server would
 * turn "this browser is a version ahead" into "this browser has no avatar",
 * which is a worse failure than a figure with a standard haircut.
 */
/**
 * The nine rails, in the order their indices appear in a seed. Append only:
 * the position in this list *is* the position in every stored seed, so
 * inserting a rail silently rewrites every avatar in the product.
 */
export const AVATAR_RAILS = ['build', 'kit', 'hair', 'eyes', 'face', 'mouth', 'acc', 'tone', 'plate'];
/**
 * What a canonical seed starts with. The `16` is the grid: a figure is
 * sixteen cells square. The full-body format has its own explicit prefix below.
 */
export const AVATAR_PREFIX = 'eos avatar set 16.';
/** Full-body customization extends the old seed without moving its indices. */
export const AVATAR_CHARACTER_RAILS = [...AVATAR_RAILS, 'hat', 'prop', 'boots', 'color', 'pose'];
export const AVATAR_CHARACTER_PREFIX = 'eos avatar set 32.';
/**
 * The largest index a rail may name. The longest rail today holds twenty-two
 * parts, so this is room to roughly triple every one of them — and it is the
 * only reason the seed has a bounded length, which is the thing a server
 * actually cares about.
 */
export const AVATAR_MAX_INDEX = 63;
const SEED = new RegExp(`^(?:${AVATAR_RAILS.map(() => '\\d{1,2}').join('\\.')})$`);
const CHARACTER_SEED = new RegExp(`^(?:${AVATAR_CHARACTER_RAILS.map(() => '\\d{1,2}').join('\\.')})$`);
/**
 * A seed claimed by a browser, canonical, or null.
 *
 * Accepts either canonical version and the bare legacy nine numbers. The prefix is
 * a label rather than data and a caller that has just joined nine indices with
 * dots should not have to know the label. What comes back always wears it, so
 * one avatar is one string wherever it is stored or compared.
 */
export function cleanSeed(raw) {
    if (typeof raw !== 'string')
        return null;
    const character = raw.startsWith(AVATAR_CHARACTER_PREFIX);
    const prefix = character ? AVATAR_CHARACTER_PREFIX : AVATAR_PREFIX;
    const body = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    if (!(character ? CHARACTER_SEED : SEED).test(body))
        return null;
    const parts = body.split('.').map(Number);
    if (parts.some((n) => n > AVATAR_MAX_INDEX))
        return null;
    // Rebuilt from the parsed numbers rather than passed through, so that "07"
    // and "7" are one seed and not two.
    return prefix + parts.join('.');
}
/** True for a seed, as opposed to an emoji face or an account's picture URL. */
export function isSeed(raw) {
    return typeof raw === 'string' && cleanSeed(raw) !== null;
}
//# sourceMappingURL=avatar.js.map